<template>
  <!-- Skeleton state -->
  <b-card v-if="loading" no-body class="card-influencer-view-skeleton mb-0 w-100">
    <div class="d-flex h-100">
      <div class="skeleton-profile-section d-flex flex-column align-items-center justify-content-center p-2">
        <b-skeleton type="avatar" class="skeleton-avatar-lg mb-1"></b-skeleton>
        <b-skeleton class="w-75 mb-50"></b-skeleton>
        <b-skeleton class="w-50 mb-50"></b-skeleton>
        <b-skeleton class="w-50 mb-1"></b-skeleton>
        <b-skeleton class="w-100 mb-50" height="32px"></b-skeleton>
        <b-skeleton class="w-100" height="32px"></b-skeleton>
      </div>
      <div class="p-2 d-flex flex-column w-100">
        <div class="d-flex mb-1">
          <div class="mr-3 w-100">
            <b-skeleton class="w-75 mb-50" height="20px"></b-skeleton>
            <b-skeleton class="w-100 mb-50"></b-skeleton>
          </div>
          <div class="w-100">
            <b-skeleton class="w-75 mb-50"></b-skeleton>
            <b-skeleton class="w-100"></b-skeleton>
          </div>
        </div>
        <b-skeleton class="w-100 mb-50"></b-skeleton>
        <b-skeleton class="w-90 mb-50"></b-skeleton>
        <b-skeleton class="w-50 mt-1" height="24px" style="border-radius: 20px;"></b-skeleton>
      </div>
      <div class="skeleton-photos-section">
        <b-skeleton class="w-100 h-100 m-0" style="border-radius: 0 12px 12px 0;"></b-skeleton>
      </div>
    </div>
  </b-card>

  <!-- Real card -->
  <b-card v-else no-body class="card-influencer-view mb-0 w-100" @click="$emit('open_side_report', data)">
    <div class="d-flex h-100">

      <!-- Left Profile -->
      <div class="profile-section d-flex flex-column align-items-center justify-content-center text-center p-2">
        <div @click.stop class="card-checkbox-view-new">
          <b-form-checkbox
            class="checkbox-card-view-new"
            v-model="selected_influencers"
            :value="index + 1"
            ref="checkboxCard"
            :class="{'opacity-0': !show_all_checkbox, 'opacity-1': show_all_checkbox}"
          >
          </b-form-checkbox>
        </div>

        <b-avatar :src="data.username.avatar" size="80px" class="mb-1" />
        <p class="font-weight-bold mb-0 text-dark influencer-name">
          {{ data.username.name.fullname }}
          <image-verified v-if="data.username.name.is_verified"/>
        </p>
        <p class="text-muted mb-0 small">@{{ data.username.name.username }}</p>
        <p class="text-muted mb-0 small mt-25 d-flex align-items-center justify-content-center geo-info">
          <feather-icon icon="MapPinIcon" size="12" class="mr-25" />
          {{ data.blogger_geo }}
        </p>
        <div class="position-actions">
          <div class="d-flex gap-50 flex-wrap">
            <b-button 
              class="blue-button w-100 text-nowrap" 
              variant="blue-button" 
              @click.stop="$emit('go_profile_report', {
                username: data.username.name.username,
                social_id: data.username.name.social_id,
              })"
            >{{ $t('search_preview.seeCompleteReport') }}</b-button>
            <add-campaign-and-list 
              :data="{username: data.username.name.username, network: network, followers: data.followers, image: data.username.avatar}"
            ></add-campaign-and-list>
          </div>
        </div>
      </div>

      <div class="p-2 d-flex flex-column w-100">
        <div class="d-flex flex-wrap">
          <div class="mb-75 mr-3">
            <div class="mb-05">
              <span class="font-weight-bold h5 mb-0">{{ data.followers_with_format }}</span>
              <span class="text-muted ml-50 small">{{ $t('search.followers') }}</span>
            </div>
            <div class="d-flex align-items-center mb-50">
              <b-avatar-group size="16px" v-if="data.networks.length > 0" class="avatar-group-nets-searcher-card">
                <b-avatar class="size-16-icons" v-for="(value, index) in data.networks" :key="index">
                  <b-img :src="getIconWhite(value)" :class="getIconColor(value, false)" fluid></b-img>
                </b-avatar>
              </b-avatar-group>
  
              <span class="ml-50 small">{{ data.total_followers }} {{ $t('youtube.total') }}</span>
              <b-tooltip target="total-help">{{ $t('search_preview.totalFollowersTooltip') }}</b-tooltip>
              <feather-icon id="total-help" icon="HelpCircleIcon" size="14" class="ml-25 text-muted cursor-pointer" />
            </div>  
          </div>
  
  
          <div class="d-flex flex-column">
            <!-- ER -->
            <div class="mb-05">
              <span class="er-status-dot mr-50" :style="`background-color: ${$t(`search_preview.engagement_nevel.${data.er_badge}`).color}`"></span>
              <span class="small">{{ data.er }}% ER</span>
              <b-tooltip target="er-help">{{ $t('search_preview.erTooltip') }}</b-tooltip>
              <feather-icon id="er-help" icon="HelpCircleIcon" size="14" class="ml-25 text-muted cursor-pointer" />
            </div>
    
            <!-- Avg level -->
            <div class="text-muted small mb-75 d-flex align-items-center flex-wrap">
              <span class="mr-25">{{ $t('campaigns.low') }}:</span>
              <feather-icon icon="HeartIcon" size="12" class="mr-25" />
              <span class="mr-50">{{ data.likes }}</span>
              <feather-icon icon="MessageCircleIcon" size="12" class="mr-25" />
              <span>{{ data.comments_avg }}</span>
            </div>
          </div>
        </div>

        <div class="w-100 avenir-medium my-1">
          <p class="description-clamp">{{ data.description }}</p>
        </div>

         <!-- PCA Badge -->
        <div v-if="data.quality" class="pca-badge-pill d-inline-flex align-items-center" :style="`background-color: ${$t(`search_preview.engagement_nevel.${data.quality.data.mark}`).pastel_color }`">
          <span class="pca-dot mr-25" :style="`background-color: ${$t(`search_preview.engagement_nevel.${data.quality.data.mark}`).color }`"></span>
          <span class="small">PCA – {{ $t(`search_preview.engagement_nevel.${data.quality.data.mark}`).text }} {{ data.quality.data.value }}</span>
          <b-tooltip target="pca-help">{{ $t('search_preview.pcaTooltip') }}</b-tooltip>
          <feather-icon id="pca-help" icon="HelpCircleIcon" size="12" class="ml-25 cursor-pointer" />
        </div>

      </div>

      <!-- Right Photos -->
      <div :class="`photos-section photos-${data.last_media.slice(0, 4).length}`" v-if="data.last_media && data.last_media.length > 0">
        <div
          v-for="(photo, i) in data.last_media.slice(0, 4)"
          :key="i"
          class="photo-thumb"
        >
          <b-img :src="photo" fluid class="h-100 w-100 image-tag" @error="onImageError"/>
        </div>
      </div>

    </div>
  </b-card>
</template>

<script>
import { BCard, BAvatar, BImg, BAvatarGroup, BButton, BTooltip, BSkeleton, BFormCheckbox } from 'bootstrap-vue'
import { getIconWhite, getIconColor } from '@/libs/utils/icons'

export default {
  name: 'cardViewInfluencerSearcher',
  components: {
    BCard,
    BAvatar,
    BImg,
    BAvatarGroup,
    BButton,
    BTooltip,
    BSkeleton,
    BFormCheckbox,
    ImageVerified: () => import('@/views/components/image/ImageVerified.vue'),
    AddCampaignAndList: () => import('@/views/components/dropdowns/addCampaignAndList.vue'),
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    index: {
      type: Number,
      default: 0
    },
    data: {
      type: Object,
      default: () => {
        return {}
      }
    },
    network: {
      type: String,
      default: 'instagram'
    },
    loading: {
      type: Boolean,
      default: false
    },
    show_all_checkbox: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    selected_influencers: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    }
  },
  methods: {
    getIconWhite,
    getIconColor,
    onImageError(event) {
      event.target.style.display = 'none'
      const thumb = event.target.closest('.photo-thumb')
      if (thumb) thumb.classList.add('photo-broken')
    },
  }
}
</script>
<style lang="scss">
.avatar-group-nets-searcher-card {
  .b-avatar-custom {
    width: 16px;
    flex-shrink: 0;
  }
}
</style>
<style scoped lang="scss">
.opacity-0 {
  opacity: 0;
}
.opacity-1 {
  opacity: 1;
}
.card-influencer-view-skeleton {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  height: 235px;

  .skeleton-profile-section {
    min-width: 300px;
    max-width: 300px;
    border-right: 1px solid #f0f0f0;

    .skeleton-avatar-lg {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
    }
  }

  .skeleton-photos-section {
    width: 235px;
    min-width: 235px;
    border-left: 1px solid #f0f0f0;
    overflow: hidden;
  }
}

.card-influencer-view {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
    cursor: pointer;

    .position-actions {
      display: flex;
    }

    .geo-info {
      opacity: 0;
    }

    .checkbox-card-view-new {
      opacity: 1;
      pointer-events: auto;
    }
  }
  
}

/* Left profile section */
.profile-section {
  min-width: 300px;
  max-width: 300px;
  border-right: 1px solid #f0f0f0;
  position: relative;

  .position-actions {
    display: none;
    width: 100%;
    height: 100%;
    position: absolute;
    flex-direction: column;
    justify-content: end;
    padding: 1em;
    top: 0;
    left: 0;
  }
}
.card-checkbox-view-new {
  position: absolute;
  top: 1em;
  left: 1em;

  .checkbox-card-view-new {
    pointer-events: none;
    transition: opacity 0.2s ease;
  
    &.checkbox-persistent {
      pointer-events: auto;
    }
  }
}

.influencer-name {
  font-size: 0.92rem;
  line-height: 1.3;
}

/* Network icon */
.network-icon-small {
  border-radius: 50%;
  margin-right: 4px;
}

/* ER status dot */
.er-status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.er-status-dot.dot-warning { background-color: #ff9f43; }
.er-status-dot.dot-success { background-color: #28c76f; }
.er-status-dot.dot-danger  { background-color: #ea5455; }

/* PCA badge */
.pca-badge-pill {
  border-radius: 20px;
  padding: 4px 12px;
  width: fit-content;
}

.pca-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.gap-50 {
  gap: 0.5rem;
}

/* Right photos strip */
.photos-section {
  display: grid;
  width: 235px;
  min-width: 235px;
  height: 235px;
  overflow: hidden;
  border-left: 1px solid #f0f0f0;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;

  &.photos-1 {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr;
  }

  &.photos-2 {
    grid-template-rows: 1fr;
    grid-template-columns: repeat(2, 1fr);
  }

  &.photos-3 {
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: repeat(2, 1fr);

    .photo-thumb:last-child {
      grid-column: span 2;
    }
  }

  &.photos-4 {
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: repeat(2, 1fr);
  }

  .image-tag {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
}

.photo-thumb {
  flex-grow: 1;
  overflow: hidden;

  &.photo-broken {
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;

    &::after {
      content: '';
      display: block;
      width: 32px;
      height: 32px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23cccccc' stroke-width='1.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpath d='M21 15l-5-5L5 21'/%3E%3C/svg%3E");
      background-size: contain;
      background-repeat: no-repeat;
    }
  }
}

.description-clamp {
  line-clamp: 2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0;
}
</style>

